import React, { useState } from 'react';
import { Printer, X, FileText, CheckCircle, ArrowRight } from 'lucide-react';
import api from '../../api/client';
import { lotesService } from '../../services/lotesService';

const TODOS_DOCUMENTOS = [
  {
    id: 'contrato-3-vias',
    label: 'Contrato 3 Vias',
    desc: 'Vendedor + Comprador + Leiloeira',
    endpoint: (orgId, loteId) => `/organizadores/${orgId}/contratos/lotes/${loteId}/contrato-3-vias`,
    color: 'orange',
  },
  {
    id: 'contrato-unico',
    label: 'Contrato Único',
    desc: 'Documento simplificado',
    endpoint: (orgId, loteId) => `/organizadores/${orgId}/contratos/lotes/${loteId}/contrato-unico`,
    color: 'blue',
  },
  {
    id: 'contrato-1-via',
    label: 'Contrato 1 Via',
    desc: 'Uma via do contrato',
    endpoint: (orgId, loteId) => `/organizadores/${orgId}/contratos/lotes/${loteId}/contrato-uma-via`,
    color: 'green',
  },
  {
    id: 'contrato-nota',
    label: 'Contrato + Nota',
    desc: 'Contrato com NP integrada',
    endpoint: (orgId, loteId) => `/organizadores/${orgId}/contratos/lotes/${loteId}/contrato-nota`,
    color: 'purple',
  },
  {
    id: 'nota-promissoria',
    label: 'Nota Promissória',
    desc: 'Nota promissória avulsa',
    endpoint: (orgId, loteId) => `/organizadores/${orgId}/contratos/lotes/${loteId}/nota-promissoria`,
    color: 'blue',
  },
  {
    id: 'nota-compromisso',
    label: 'Nota Compromisso',
    desc: 'Nota de compromisso',
    endpoint: (orgId, loteId) => `/organizadores/${orgId}/contratos/lotes/${loteId}/nota-compromisso`,
    color: 'orange',
  },
  {
    id: 'resumo-fatura',
    label: 'Resumo Fatura',
    desc: 'Dados financeiros do lote',
    endpoint: (orgId, loteId) => `/organizadores/${orgId}/contratos/lotes/${loteId}/resumo-fatura`,
    color: 'gray',
  },
];

const ModalImpressaoPosFaturamento = ({ lote, organizador, opcoesSelecionadas, onClose, mostrarNovoLote = false, leilaoId = null, onIrParaLote }) => {
  const [gerando, setGerando] = useState(null);
  const [novoLoteNumero, setNovoLoteNumero] = useState('');
  const [buscandoLote, setBuscandoLote] = useState(false);
  const [novoLoteErro, setNovoLoteErro] = useState('');

  // Filtrar apenas os documentos selecionados na config
  const opcoes = TODOS_DOCUMENTOS.filter(doc => (opcoesSelecionadas || []).includes(doc.id));

  // Se não tem opções configuradas, não renderiza nada
  if (opcoes.length === 0 && !(mostrarNovoLote && leilaoId)) {
    onClose();
    return null;
  }

  const handleImprimir = async (opcao) => {
    try {
      setGerando(opcao.id);
      const url = opcao.endpoint(organizador.id, lote.id);
      const response = await api.get(url, {
        responseType: 'blob',
        timeout: 60000,
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);

      // Abrir em nova aba para impressão
      const win = window.open(blobUrl, '_blank');
      if (win) {
        win.addEventListener('load', () => {
          setTimeout(() => win.print(), 500);
        });
      }
    } catch (error) {
      console.error('Erro ao gerar documento:', error);
      alert('Erro ao gerar documento. Tente novamente.');
    } finally {
      setGerando(null);
    }
  };

  const handleIrParaLote = async () => {
    const numero = novoLoteNumero.trim();
    if (!numero || buscandoLote) return;
    try {
      setBuscandoLote(true);
      setNovoLoteErro('');
      const lotes = await lotesService.listarLotesPorLeilao(organizador.id, leilaoId);
      const norm = (v) => String(v ?? '').trim().toLowerCase();
      const alvo = (lotes || []).find(l => norm(l.numero_lote) === norm(numero));
      if (!alvo) {
        setNovoLoteErro(`Lote ${numero} não encontrado neste leilão`);
        return;
      }
      if (String(alvo.id) === String(lote.id)) {
        setNovoLoteErro('Este é o lote que acabou de ser faturado');
        return;
      }
      onIrParaLote?.(alvo);
    } catch (error) {
      console.error('Erro ao buscar lote:', error);
      setNovoLoteErro('Erro ao buscar o lote. Tente novamente.');
    } finally {
      setBuscandoLote(false);
    }
  };

  const colorMap = {
    blue: 'bg-blue-50 border-blue-200 hover:bg-blue-100 text-blue-700',
    green: 'bg-green-50 border-green-200 hover:bg-green-100 text-green-700',
    purple: 'bg-purple-50 border-purple-200 hover:bg-purple-100 text-purple-700',
    orange: 'bg-orange-50 border-orange-200 hover:bg-orange-100 text-orange-700',
    gray: 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700',
  };

  const iconColorMap = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
    orange: 'text-orange-600',
    gray: 'text-gray-600',
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-full">
              <CheckCircle size={24} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Lote Faturado!</h2>
              <p className="text-sm text-gray-500">Lote {lote.numero_lote} — Deseja imprimir?</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Opções de impressão */}
        <div className="p-5 space-y-3">
          {opcoes.map((opcao) => (
            <button
              key={opcao.id}
              onClick={() => handleImprimir(opcao)}
              disabled={gerando !== null}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${colorMap[opcao.color]} ${
                gerando === opcao.id ? 'opacity-70' : ''
              }`}
            >
              <div className={`p-2 rounded-lg bg-white/80 ${iconColorMap[opcao.color]}`}>
                {gerando === opcao.id ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                ) : (
                  <FileText size={20} />
                )}
              </div>
              <div className="flex-1 text-left">
                <span className="font-medium text-sm">{opcao.label}</span>
                <p className="text-xs opacity-75">{opcao.desc}</p>
              </div>
              <Printer size={16} className="opacity-60" />
            </button>
          ))}
        </div>

        {/* Novo lote */}
        {mostrarNovoLote && leilaoId && (
          <div className="px-5 pb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Faturar novo lote</label>
            <div className="flex gap-2">
              <input
                type="text"
                autoFocus
                value={novoLoteNumero}
                onChange={(e) => { setNovoLoteNumero(e.target.value); setNovoLoteErro(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleIrParaLote(); } }}
                placeholder="Nº do lote"
                className="flex-1 px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-green-500"
              />
              <button
                onClick={handleIrParaLote}
                disabled={!novoLoteNumero.trim() || buscandoLote}
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-lg transition-colors"
              >
                Ir <ArrowRight size={14} />
              </button>
            </div>
            {novoLoteErro && <p className="text-xs text-red-600 mt-1">{novoLoteErro}</p>}
          </div>
        )}

        {/* Footer */}
        <div className="px-5 pb-5">
          <button
            onClick={onClose}
            className="w-full py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Fechar sem imprimir
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalImpressaoPosFaturamento;
